// Model + helpers for the artwork template body the matchcard engine consumes
// (apps/api/shared/matchcard_generator/matchcard_engine.py), schema
// tc.artwork.template.v2. Every field here is one the engine actually reads, so
// the round-trip preview can never render something the editor can't express.
//
// Body: { name, canvas, targeting, layers[] }.
//
// Two things about the schema shape the whole editor:
//
//  1. It is STRICT (additionalProperties: false). Publish rejects any key it
//     doesn't know, so unlike the old v1 model there is no "keep unknown keys
//     around" escape hatch — a stray key is a publish failure, not a no-op.
//     Layers therefore have no index signature, and editor-only state is kept
//     outside the body.
//  2. Layers are a TREE. A group layer holds `then` / `else` arms of nested
//     layers, and a group has no id of its own (the schema forbids one), so
//     layers are addressed by path rather than by id. See LayerPath.

// ---------------------------------------------------------------------------
// Layer types
// ---------------------------------------------------------------------------

export type LayerType = 'image' | 'text' | 'shape' | 'group'

// The 9 anchor points, in 3×3 grid reading order (row-major). The engine also
// accepts 'left'/'right' as aliases of 'center-left'/'center-right'; we
// normalize those on read so the grid has one cell per position.
export type AnchorPoint =
  | 'top-left'
  | 'top'
  | 'top-right'
  | 'center-left'
  | 'center'
  | 'center-right'
  | 'bottom-left'
  | 'bottom'
  | 'bottom-right'

export const ANCHOR_POINTS: AnchorPoint[] = [
  'top-left',
  'top',
  'top-right',
  'center-left',
  'center',
  'center-right',
  'bottom-left',
  'bottom',
  'bottom-right',
]

const ANCHOR_ALIASES: Record<string, AnchorPoint> = {
  left: 'center-left',
  right: 'center-right',
}

export const normalizeAnchor = (raw: unknown): AnchorPoint => {
  const s = typeof raw === 'string' ? raw : ''
  if (ANCHOR_ALIASES[s]) return ANCHOR_ALIASES[s]
  return (ANCHOR_POINTS as string[]).includes(s) ? (s as AnchorPoint) : 'top-left'
}

export type Team = 'home' | 'away'

/** Fields a text layer can bind to on a player. */
export type BoundField = 'first_name' | 'last_name' | 'full_name'

/** Per-player assets an image layer can bind to. */
export type EntityAsset = 'headshot' | 'flag'

export type TextTransform = 'uppercase' | 'lowercase' | 'titlecase'

export type ImageFit = 'cover' | 'width' | 'stretch'

export type MatchType = 'singles' | 'doubles' | 'mixed_doubles'

// ---------------------------------------------------------------------------
// Body shapes (mirroring tc.artwork.template.v2 exactly)
// ---------------------------------------------------------------------------

export type Point = [number, number]
export type NullableSize = [number | null, number | null]

export interface AnchorTo {
  layer: string
  anchor: AnchorPoint
  self?: AnchorPoint
  offset?: Point
}

/** An image reference: the pinned id plus the s3_key kept as a human label. */
export interface ImageRef {
  tc_image_id: string | null
  label?: string
}

export interface FixedSource extends ImageRef {
  kind: 'fixed'
}

export interface EntitySource {
  kind: 'entity'
  /** Always "<team>.players[<n>]" — the schema pattern allows nothing else. */
  entity: string
  asset: EntityAsset
}

export interface ConditionalRule extends ImageRef {
  when: { category_in: string[] }
}

export interface ConditionalSource {
  kind: 'conditional'
  rules: ConditionalRule[]
  default?: ImageRef
}

export type ImageSource = FixedSource | EntitySource | ConditionalSource

export interface ImageBox {
  at?: Point
  size?: NullableSize
  /** 'canvas' stretches the asset over the whole frame (the background case). */
  fill?: 'canvas'
  fit?: ImageFit
}

export interface ImageLayer {
  id: string
  type: 'image'
  z?: number
  box?: ImageBox
  source: ImageSource
  fallback?: { asset: EntityAsset; size: Point; align?: 'center' }
  reserve_space?: boolean
  assumed_ratio?: number
  skip_if_fell_back?: string
  anchor_to?: AnchorTo
  at?: Point
}

export type TextContent = { static: string } | { bound: string }

export interface TextLayer {
  id: string
  type: 'text'
  z?: number
  content: TextContent
  font: { tc_font_id: string | null; label?: string; size: number }
  max_width?: number
  min_size?: number
  transform?: TextTransform
  color?: string
  anchor_to?: AnchorTo
  at?: Point
}

export interface ShapeLayer {
  id: string
  type: 'shape'
  z?: number
  size: Point
  color?: string
  opacity?: number
  skew_x?: number
  anchor_to?: AnchorTo
  at?: Point
}

/** A group's condition. `path` alone is truthiness (the headshot check);
 * hero templates add `equals`/`in` over the match lifecycle phase. */
export interface GroupWhen {
  path: string
  equals?: string
  in?: string[]
}

/** A conditional group. Note: no `id` — the schema forbids one. */
export interface GroupLayer {
  type: 'group'
  when: GroupWhen
  then?: Layer[]
  else?: Layer[]
}

export type Layer = ImageLayer | TextLayer | ShapeLayer | GroupLayer

export interface Targeting {
  categories?: string[]
  types: MatchType[]
  resolution: string
  default_for_unknown_category?: boolean
}

export interface TemplateBody {
  name: string
  canvas: { width: number; height: number }
  targeting: Targeting
  layers: Layer[]
}

// Narrowing helpers — `type` is the discriminant everywhere.
export const isGroup = (l: Layer): l is GroupLayer => l.type === 'group'
export const isImage = (l: Layer): l is ImageLayer => l.type === 'image'
export const isText = (l: Layer): l is TextLayer => l.type === 'text'
export const isShape = (l: Layer): l is ShapeLayer => l.type === 'shape'

/** Every layer type except group carries an id and can be positioned. */
export type PlacedLayer = ImageLayer | TextLayer | ShapeLayer
export const isPlaced = (l: Layer): l is PlacedLayer => l.type !== 'group'

// ---------------------------------------------------------------------------
// Layer paths
//
// Groups nest, and they have no id, so a layer is addressed by its position in
// the tree: a list of array indices with 'then'/'else' marking which arm of a
// group to descend into. [3, 'then', 0] is layers[3].then[0].
// ---------------------------------------------------------------------------

export type GroupArm = 'then' | 'else'
export type LayerPath = Array<number | GroupArm>

export const pathKey = (path: LayerPath): string => path.join('.')

export const samePath = (a: LayerPath | null, b: LayerPath | null): boolean =>
  a !== null && b !== null && pathKey(a) === pathKey(b)

/** The layer at `path`, or null if the path doesn't resolve. */
export function getLayerAt(layers: Layer[], path: LayerPath): Layer | null {
  let list: Layer[] = layers
  let layer: Layer | null = null
  for (const step of path) {
    if (typeof step === 'number') {
      layer = list[step] ?? null
      if (!layer) return null
    } else {
      if (!layer || !isGroup(layer)) return null
      list = layer[step] ?? []
      layer = null
    }
  }
  return layer
}

/**
 * Rebuild the tree with `fn` applied to the layer at `path`. Returning null
 * from `fn` deletes it. Structural sharing isn't attempted — bodies are small
 * and every edit already goes through a JSON round trip for history.
 */
export function mapLayerAt(
  layers: Layer[],
  path: LayerPath,
  fn: (layer: Layer) => Layer | null
): Layer[] {
  const walk = (list: Layer[], prefix: LayerPath): Layer[] => {
    const out: Layer[] = []
    list.forEach((layer, i) => {
      const here: LayerPath = [...prefix, i]
      if (pathKey(here) === pathKey(path)) {
        const next = fn(layer)
        if (next) out.push(next)
        return
      }
      if (isGroup(layer)) {
        const arms: GroupLayer = { ...layer }
        if (layer.then) arms.then = walk(layer.then, [...here, 'then'])
        if (layer.else) arms.else = walk(layer.else, [...here, 'else'])
        out.push(arms)
        return
      }
      out.push(layer)
    })
    return out
  }
  return walk(layers, [])
}

/** Patch the (placed) layer at `path`. */
export function patchLayerAt(
  layers: Layer[],
  path: LayerPath,
  patch: Record<string, unknown>
): Layer[] {
  return mapLayerAt(layers, path, (layer) => ({ ...layer, ...patch }) as Layer)
}

export function removeLayerAt(layers: Layer[], path: LayerPath): Layer[] {
  return mapLayerAt(layers, path, () => null)
}

/** Append `layer` into the list a path points at (a group arm, or the root). */
export function appendLayer(
  layers: Layer[],
  parent: { path: LayerPath; arm: GroupArm } | null,
  layer: Layer
): { layers: Layer[]; path: LayerPath } {
  if (!parent) {
    return { layers: [...layers, layer], path: [layers.length] }
  }
  const group = getLayerAt(layers, parent.path)
  if (!group || !isGroup(group)) return { layers, path: parent.path }
  const arm = group[parent.arm] ?? []
  const next = mapLayerAt(layers, parent.path, (g) => ({
    ...(g as GroupLayer),
    [parent.arm]: [...arm, layer],
  }))
  return { layers: next, path: [...parent.path, parent.arm, arm.length] }
}

/** Every placed layer in the tree, with its path, in declaration order. */
export function flattenLayers(
  layers: Layer[],
  prefix: LayerPath = []
): { layer: Layer; path: LayerPath }[] {
  const out: { layer: Layer; path: LayerPath }[] = []
  layers.forEach((layer, i) => {
    const here: LayerPath = [...prefix, i]
    out.push({ layer, path: here })
    if (isGroup(layer)) {
      out.push(...flattenLayers(layer.then ?? [], [...here, 'then']))
      out.push(...flattenLayers(layer.else ?? [], [...here, 'else']))
    }
  })
  return out
}

/** Two layers in sibling arms of one group never appear in the same render. */
const mutuallyExclusive = (a: LayerPath, b: LayerPath): boolean => {
  const shared = Math.min(a.length, b.length)
  for (let i = 0; i < shared; i++) {
    if (a[i] !== b[i]) {
      // Diverging on an arm name means one is in `then`, the other in `else`.
      return typeof a[i] === 'string' && typeof b[i] === 'string'
    }
  }
  return false
}

/**
 * Ids a layer at `ownPath` may reference — from anchor_to.layer or
 * skip_if_fell_back — plus the 'canvas' pseudo-layer.
 *
 * Only layers the engine has already drawn qualify. render_with_boxes adds a
 * layer's box *after* drawing it, in ascending z, and resolve_position falls
 * back to the absolute position when the reference has no box yet. So an anchor
 * pointing at a higher-z layer doesn't error — it silently ignores anchor_to,
 * which leaves the layer stuck: the form shows Offset X/Y, dragging snaps back,
 * and typing an offset moves it nowhere.
 *
 * Ties matter: equal z keeps body order, the same stable sort the engine uses,
 * so a same-z layer declared earlier is legitimately available.
 *
 * Layers in a sibling group arm are excluded too — they never co-render, so
 * such a reference could never resolve.
 */
export function anchorTargets(layers: Layer[], ownPath: LayerPath): string[] {
  const placed = flattenLayers(layers).filter(
    (entry): entry is { layer: PlacedLayer; path: LayerPath } => isPlaced(entry.layer)
  )
  const drawOrder = byZ(placed)
  const rank = new Map(drawOrder.map((entry, i) => [pathKey(entry.path), i]))
  const ownRank = rank.get(pathKey(ownPath))
  if (ownRank === undefined) return ['canvas']

  const ids = drawOrder
    .filter(
      (entry) =>
        (rank.get(pathKey(entry.path)) ?? 0) < ownRank &&
        !mutuallyExclusive(entry.path, ownPath)
    )
    .map((entry) => entry.layer.id)
    .filter(Boolean)
  return ['canvas', ...new Set(ids)]
}

// ---------------------------------------------------------------------------
// Bound / entity paths
// ---------------------------------------------------------------------------

/** "home.players[0]" — the only entity shape the schema allows. */
export const entityPath = (team: Team, index: number): string =>
  `${team}.players[${index}]`

export const boundPath = (team: Team, index: number, field: BoundField): string =>
  `${entityPath(team, index)}.${field}`

export interface ParsedEntity {
  team: Team
  index: number
}

export function parseEntity(path: string | undefined): ParsedEntity {
  const m = /^(home|away)\.players\[(\d+)\]/.exec(path ?? '')
  return m ? { team: m[1] as Team, index: Number(m[2]) } : { team: 'home', index: 0 }
}

export function parseBound(
  path: string | undefined
): ParsedEntity & { field: BoundField } {
  const m = /^(home|away)\.players\[(\d+)\]\.(first_name|last_name|full_name)$/.exec(
    path ?? ''
  )
  return m
    ? { team: m[1] as Team, index: Number(m[2]), field: m[3] as BoundField }
    : { team: 'home', index: 0, field: 'last_name' }
}

// ---------------------------------------------------------------------------
// Group conditions
//
// A group's `when` decides which arm renders. The matchcard schema permits
// exactly one shape — ^(home|away)\.all_have_headshots$ — and the hero schema
// (tc.artwork.hero.v1) adds phase equality over the match lifecycle.
//
// It's a table rather than a special case because the engine can already
// evaluate more than the schema exposes: condition_holds handles `equals` and
// `in`, and DataContext.resolve handles `category` plus any player field
// (including a single player's has_headshot). Widening the schema should be a
// matter of adding entries here. A second one would read:
//
//   {
//     value: 'player_has_headshot',
//     label: 'A specific player has a headshot',
//     params: ['team', 'playerIndex'],
//     build: (p) => `${p.team}.players[${p.playerIndex ?? 0}].has_headshot`,
//     match: (path) => {
//       const m = /^(home|away)\.players\[(\d+)\]\.has_headshot$/.exec(path)
//       return m ? { team: m[1] as Team, playerIndex: Number(m[2]) } : null
//     },
//   }
// ---------------------------------------------------------------------------

/** Controls a condition kind needs, beyond the choice of kind itself. */
export type GroupConditionParam = 'team' | 'playerIndex' | 'phase'

/** The hero-card lifecycle phases a hero template's groups may branch on.
 * Mirrors HERO_PHASE_* in shared/matchcard_generator/constants.py. */
export const HERO_PHASES = ['upcoming', 'live', 'ended', 'replay'] as const
export type HeroPhase = (typeof HERO_PHASES)[number]

export interface GroupConditionParams {
  team: Team
  playerIndex?: number
  phase?: HeroPhase
}

export interface GroupConditionKind {
  value: string
  label: string
  params: GroupConditionParam[]
  /** Offered only when editing a hero template (the matchcard schema rejects
   * the condition, so the matchcard editor must not build it). */
  heroOnly?: boolean
  /** The `when` object these params produce. */
  build: (params: GroupConditionParams) => GroupWhen
  /** Params recovered from a stored `when`, or null if this kind doesn't own it. */
  match: (when: GroupWhen) => GroupConditionParams | null
}

export const GROUP_CONDITIONS: GroupConditionKind[] = [
  {
    value: 'team_all_have_headshots',
    label: 'Every player on a team has a headshot',
    params: ['team'],
    build: (p) => ({ path: `${p.team}.all_have_headshots` }),
    match: (when) => {
      if (when.equals !== undefined || when.in !== undefined) return null
      const m = /^(home|away)\.all_have_headshots$/.exec(when.path ?? '')
      return m ? { team: m[1] as Team } : null
    },
  },
  {
    value: 'match_phase',
    label: 'Match lifecycle phase is…',
    params: ['phase'],
    heroOnly: true,
    // `equals` only from the editor; a hand-authored `in: [...]` list is still
    // schema-valid but shows as an unrecognised condition here.
    build: (p) => ({ path: 'phase', equals: p.phase ?? 'upcoming' }),
    match: (when) =>
      when.path === 'phase' &&
      typeof when.equals === 'string' &&
      (HERO_PHASES as readonly string[]).includes(when.equals)
        ? { team: 'home', phase: when.equals as HeroPhase }
        : null,
  },
]

/** The kind that owns a stored `when`, plus its params. Null when unrecognised. */
export function parseGroupCondition(
  when: GroupWhen | undefined
): { kind: GroupConditionKind; params: GroupConditionParams } | null {
  if (!when) return null
  for (const kind of GROUP_CONDITIONS) {
    const params = kind.match(when)
    if (params) return { kind, params }
  }
  return null
}

/** The `when` object for a kind + params, used when either changes. */
export const groupConditionWhen = (
  kind: GroupConditionKind,
  params: GroupConditionParams
): GroupWhen => kind.build(params)

// ---------------------------------------------------------------------------
// Layer presentation
// ---------------------------------------------------------------------------

export interface LayerChip {
  color: string
  letter: string
  label: string
}

export const LAYER_CHIPS: Record<LayerType, LayerChip> = {
  image: { color: '#2D6A6D', letter: 'I', label: 'Image' },
  text: { color: '#1D483C', letter: 'T', label: 'Text' },
  shape: { color: '#00875D', letter: 'S', label: 'Shape' },
  group: { color: '#5a6b60', letter: 'G', label: 'Group' },
}

export const LAYER_TYPE_LABELS: Record<LayerType, string> = {
  image: 'Image',
  text: 'Text',
  shape: 'Shape',
  group: 'Conditional group',
}

export const ADDABLE_LAYER_TYPES: LayerType[] = ['text', 'image', 'shape', 'group']

/**
 * Where an image layer gets its picture, as one flat list instead of a kind
 * dropdown plus team, player-index and asset dropdowns.
 *
 * The three entity axes only ever produce a handful of combinations, and "index"
 * was the schema's word for which player on the team — 0-based, meaningless in
 * singles, and jargon in doubles. Spelling the combinations out ("Away player 2
 * — country flag") removes the concept.
 */
export interface ImageSourceChoice {
  value: string
  label: string
  kind: ImageSource['kind']
  entity?: { team: Team; index: number }
  asset?: EntityAsset
}

const ENTITY_ASSET_LABELS: Record<EntityAsset, string> = {
  headshot: 'headshot',
  flag: 'country flag',
}

/** The stable key for a source, matching an ImageSourceChoice value. */
export function imageSourceKey(source: ImageSource): string {
  if (source.kind !== 'entity') return source.kind
  const { team, index } = parseEntity(source.entity)
  return `entity:${team}:${index}:${source.asset}`
}

/**
 * Choices for a layer, given what the template targets. A second player is only
 * offered when the template covers doubles — in a singles render `players[1]`
 * resolves to nothing, so the layer would silently draw nothing.
 *
 * The layer's current source is always included, even if the targeting no longer
 * offers it, so opening a template can't quietly change what it points at.
 */
export function imageSourceChoices(
  targeting: Targeting | undefined,
  current: ImageSource
): ImageSourceChoice[] {
  const playerCount = targetsTwoPlayerTeams(targeting) ? 2 : 1

  const choices: ImageSourceChoice[] = []
  for (const team of ['home', 'away'] as Team[]) {
    for (let index = 0; index < playerCount; index++) {
      for (const asset of ['headshot', 'flag'] as EntityAsset[]) {
        choices.push({
          value: `entity:${team}:${index}:${asset}`,
          label:
            `${team === 'home' ? 'Home' : 'Away'}` +
            `${playerCount > 1 ? ` player ${index + 1}` : ''}` +
            ` — ${ENTITY_ASSET_LABELS[asset]}`,
          kind: 'entity',
          entity: { team, index },
          asset,
        })
      }
    }
  }
  choices.push({ value: 'fixed', label: 'A fixed image', kind: 'fixed' })
  choices.push({
    value: 'conditional',
    // Field-agnostic, because the field is its own control below — the label
    // used to name `category` and would have gone stale the moment a second
    // field existed.
    label: 'A different image depending on the match…',
    kind: 'conditional',
  })

  const key = imageSourceKey(current)
  if (!choices.some((c) => c.value === key) && current.kind === 'entity') {
    const { team, index } = parseEntity(current.entity)
    choices.unshift({
      value: key,
      label:
        `${team === 'home' ? 'Home' : 'Away'} player ${index + 1} — ` +
        `${ENTITY_ASSET_LABELS[current.asset] ?? current.asset}` +
        ' (not in this template’s targeting)',
      kind: 'entity',
      entity: { team, index },
      asset: current.asset,
    })
  }
  return choices
}

/**
 * The source a choice produces, keeping the previous one's data when the kind is
 * unchanged — so flipping to a player asset and back doesn't lose a pinned image
 * or a table of category rules.
 */
export function sourceFromChoice(
  choice: ImageSourceChoice,
  previous: ImageSource
): ImageSource {
  if (choice.kind === 'entity' && choice.entity && choice.asset) {
    return {
      kind: 'entity',
      entity: entityPath(choice.entity.team, choice.entity.index),
      asset: choice.asset,
    }
  }
  if (choice.kind === 'fixed') {
    return previous.kind === 'fixed' ? previous : { kind: 'fixed', tc_image_id: null }
  }
  return previous.kind === 'conditional' ? previous : { kind: 'conditional', rules: [] }
}

export const ENTITY_ASSET_OPTIONS: { value: EntityAsset; label: string }[] = [
  { value: 'headshot', label: 'Headshot' },
  { value: 'flag', label: 'Flag' },
]

export const BOUND_FIELD_OPTIONS: { value: BoundField; label: string }[] = [
  { value: 'last_name', label: 'Last name' },
  { value: 'first_name', label: 'First name' },
  { value: 'full_name', label: 'Full name' },
]

// Match-level text bindings — hero templates only (tc.artwork.hero.v1); the
// matchcard schema rejects them. Pre-formatted US-Eastern display strings
// supplied by the render pipeline's data context.
export type MatchBoundField = 'start_date_display' | 'start_time_display'

export const MATCH_BOUND_OPTIONS: { value: MatchBoundField; label: string }[] = [
  { value: 'start_date_display', label: 'Start date ("Sunday, Aug 10")' },
  { value: 'start_time_display', label: 'Start time ("2:05 PM ET")' },
]

export const matchBoundPath = (field: MatchBoundField): string => `match.${field}`

export function parseMatchBound(path: string | undefined): MatchBoundField | null {
  const m = /^match\.(start_date_display|start_time_display)$/.exec(path ?? '')
  return m ? (m[1] as MatchBoundField) : null
}

export const TEAM_OPTIONS: { value: Team; label: string }[] = [
  { value: 'home', label: 'Home' },
  { value: 'away', label: 'Away' },
]

export const TEXT_TRANSFORM_OPTIONS: { value: TextTransform | ''; label: string }[] = [
  { value: '', label: 'As typed' },
  { value: 'uppercase', label: 'UPPERCASE' },
  { value: 'lowercase', label: 'lowercase' },
  { value: 'titlecase', label: 'Titlecase' },
]

/**
 * How an image is sized, as six explicit modes.
 *
 * The stored schema spreads this across three things — `box.fill`, `box.fit`,
 * and whether each `box.size` entry is a number, null, or absent — so "no fit"
 * alone has four different outcomes, including a trap where omitting `size`
 * silently renders the image 60px tall. These modes are the outcomes instead,
 * and each writes exactly the keys the renderer reads for it.
 *
 * CSS equivalents, where there is one: `exact` is object-fit: fill, `cover` is
 * object-fit: cover with object-position: center, `canvas` is object-fit: fill
 * at canvas size. `contain` has no equivalent — nothing in the renderer
 * letterboxes, so an image can't be bounded by both sides without distorting or
 * cropping.
 */
export type SizingMode =
  | 'canvas'
  | 'cover'
  | 'exact'
  | 'fixedWidth'
  | 'fixedHeight'
  | 'canvasWidth'

/** Which width/height inputs a mode actually reads. */
export type SizingFields = 'none' | 'both' | 'width' | 'height'

export const SIZING_MODES: {
  value: SizingMode
  label: string
  help: string
  fields: SizingFields
}[] = [
  {
    value: 'canvas',
    label: 'Whole canvas (background)',
    help:
      'Stretched to exactly the canvas, so proportions are not preserved — use ' +
      'art already cut to this size. Registers no box, so it has no handles and ' +
      'nothing can anchor to it. CSS: object-fit: fill at canvas size.',
    fields: 'none',
  },
  {
    value: 'cover',
    label: 'Cover a box, crop the overflow',
    help:
      'Scales until the image covers the box, then centre-crops the excess. ' +
      'Never distorts. This is how headshots are placed. CSS: object-fit: cover ' +
      'with object-position: center.',
    fields: 'both',
  },
  {
    value: 'exact',
    label: 'Exact size (distorts)',
    help:
      'Resizes to exactly these dimensions, squashing the image if they don’t ' +
      'match its proportions. CSS: object-fit: fill.',
    fields: 'both',
  },
  {
    value: 'fixedWidth',
    label: 'Fixed width, auto height',
    help: 'Scales to this width; the height follows the image’s proportions.',
    fields: 'width',
  },
  {
    value: 'fixedHeight',
    label: 'Fixed height, auto width',
    help:
      'Scales to this height; the width follows the image’s proportions. This is ' +
      'how the flags are placed.',
    fields: 'height',
  },
  {
    value: 'canvasWidth',
    label: 'Full canvas width, auto height',
    help:
      'Scales the image so its width matches the canvas — no dimensions of its ' +
      'own. Equivalent to a fixed width set to the canvas width, so it’s a ' +
      'shorthand that tracks the canvas rather than a distinct behaviour. The ' +
      'seeded plates use it.',
    fields: 'none',
  },
]

const SIZING_FIELDS: Record<SizingMode, SizingFields> = SIZING_MODES.reduce(
  (acc, m) => ({ ...acc, [m.value]: m.fields }),
  {} as Record<SizingMode, SizingFields>
)

export const sizingFieldsFor = (mode: SizingMode): SizingFields => SIZING_FIELDS[mode]

/**
 * The mode a stored box is in, or null when it's a state the modes can't
 * express — no `size` key (renders 60px tall), `[null, null]` (intrinsic size),
 * or the unimplemented `fit: "stretch"`. None of those occur in any seeded body;
 * they're surfaced rather than silently coerced so a hand-edited body isn't
 * quietly rewritten.
 */
export function sizingModeOf(box: ImageBox | undefined): SizingMode | null {
  const b = box ?? {}
  if (b.fill === 'canvas') return 'canvas'
  if (b.fit === 'width') return 'canvasWidth'
  if (b.fit === 'cover') return 'cover'
  if (b.fit) return null // 'stretch' — in the enum, no branch in the renderer
  if (!b.size) return null // defaults to [null, 60] at render time
  const [w, h] = b.size
  if (w !== null && h !== null) return 'exact'
  if (w !== null) return 'fixedWidth'
  if (h !== null) return 'fixedHeight'
  return null // [null, null] — intrinsic size
}

/**
 * The box for `mode`, carrying over dimensions the new mode still reads and
 * dropping the keys it doesn't. Dropping matters: leaving an ignored `size` on a
 * canvas fill would publish a body that says something the renderer never looks
 * at. Switching away and back therefore loses the old numbers — undo covers it.
 */
export function boxForSizingMode(
  box: ImageBox | undefined,
  mode: SizingMode,
  canvas: { width: number; height: number }
): ImageBox {
  const b = box ?? {}
  const [w, h] = b.size ?? [null, null]
  const at = b.at
  const defaultW = w ?? Math.round(canvas.width / 4)
  const defaultH = h ?? 60

  switch (mode) {
    case 'canvas':
      // Position is never read for a canvas fill, so it goes too.
      return { fill: 'canvas' }
    case 'canvasWidth':
      return { ...(at ? { at } : {}), fit: 'width' }
    case 'cover':
      return { ...(at ? { at } : {}), size: [defaultW, h ?? defaultW], fit: 'cover' }
    case 'exact':
      return { ...(at ? { at } : {}), size: [defaultW, h ?? defaultH] }
    case 'fixedWidth':
      return { ...(at ? { at } : {}), size: [defaultW, null] }
    case 'fixedHeight':
      return { ...(at ? { at } : {}), size: [null, defaultH] }
  }
}

// targeting.categories values the seeded templates use. Free text is still
// allowed — this is the picker's shortlist, not a validation set.
export const TARGETING_CATEGORIES = [
  'ATP',
  'WTA',
  'ITF Men',
  'ITF Women',
  'Davis Cup',
  'Billie Jean King Cup',
  'Challenger',
]

// ---------------------------------------------------------------------------
// Conditional image fields
//
// A conditional source picks an image by looking at the match. Today the schema
// allows exactly one field: `when: { category_in: [...] }`, additionalProperties
// false. So there is one entry below and the field dropdown has one option.
//
// It's a table because the engine already has a general condition mechanism it
// isn't using here — condition_holds(when, resolve) handles {path, in: [...]}
// over any path DataContext resolves, which is what group layers use. When the
// conditional source is moved onto it, `category_in` becomes
// {path: 'category', in: [...]} and a new field is an entry here plus a path
// exposed on DataContext. `path` below is already the future wire form; `storage`
// is how it's written until then.
//
// A second entry would read:
//
//   {
//     value: 'surface',
//     label: 'Court surface',
//     path: 'surface',
//     storage: null,            // needs the unified `when` shape
//     suggestions: ['Hard', 'Clay', 'Grass', 'Carpet'],
//   }
//
// (tc_season.tc_surface / v_season.effective_surface already exist; what's
// missing is carrying it onto MatchcardData and exposing it on DataContext.)
// ---------------------------------------------------------------------------

export interface ConditionalField {
  value: string
  label: string
  /** The `when.path` this becomes once conditions are unified. */
  path: string
  /** The key a rule stores today, or null for a field needing the new shape. */
  storage: 'category_in' | null
  /** Offered as chips; the value is free text, so these only suggest. */
  suggestions: string[]
}

export const CONDITIONAL_FIELDS: ConditionalField[] = [
  {
    value: 'category',
    label: 'Category',
    path: 'category',
    storage: 'category_in',
    suggestions: TARGETING_CATEGORIES,
  },
]

/**
 * Which field a conditional source keys off.
 *
 * Inferred from the rules rather than stored, because today's schema has nowhere
 * to put it — `category_in` is both the field and the values. Once `when` carries
 * a path, this reads that instead.
 */
export function conditionalFieldOf(source: ImageSource): ConditionalField {
  if (source.kind === 'conditional') {
    const stored = source.rules?.find((r) => r.when && 'category_in' in r.when)
    if (stored) return CONDITIONAL_FIELDS[0]
  }
  return CONDITIONAL_FIELDS[0]
}

export const MATCH_TYPES: MatchType[] = ['singles', 'doubles', 'mixed_doubles']

/**
 * The match types selection can actually ask for.
 *
 * select_template derives its type as `"doubles" if is_doubles else "singles"`,
 * and is_doubles is already true for a mixed-doubles competition
 * (data_fetcher: `competition_type in ("doubles", "mixed_doubles")`). So
 * 'mixed_doubles' never appears on the asking side: a mixed-doubles match is
 * matched by 'doubles'. It's in the schema enum, and the seeded doubles cards
 * list it alongside 'doubles' where it's redundant — but a template listing only
 * 'mixed_doubles' can never be selected.
 */
const SELECTABLE_MATCH_TYPES: MatchType[] = ['singles', 'doubles']

/** Whether any match could select a template with this targeting. */
export const targetingIsSelectable = (targeting: Targeting | undefined): boolean =>
  (targeting?.types ?? []).some((t) => SELECTABLE_MATCH_TYPES.includes(t))

/** True for a type that's accepted but can never be matched on its own. */
export const matchTypeIsRedundant = (type: MatchType): boolean =>
  !SELECTABLE_MATCH_TYPES.includes(type)

/**
 * Whether a template can render two players per side. Same field the engine
 * selects on, so the editor offers exactly the player slots a match reaching
 * this template could have.
 */
export const targetsTwoPlayerTeams = (targeting: Targeting | undefined): boolean =>
  (targeting?.types ?? []).some((t) => t === 'doubles' || t === 'mixed_doubles')

/**
 * Canvas presets.
 *
 * `selectable` is whether the engine can ever pick a template with this aspect.
 * MatchcardEngine._ASPECT_RATIOS knows only 16:9 and 4:3, and every caller asks
 * for one of those, so a square or portrait template renders correctly on demand
 * (preview, the dev script) but is never chosen for a real card. Marked rather
 * than hidden: the canvas has to exist before anything can request it.
 */
export const CANVAS_PRESETS: {
  value: string
  label: string
  width: number
  height: number
  selectable: boolean
}[] = [
  { value: '1920x1080', label: '16:9 landscape — 1920×1080', width: 1920, height: 1080, selectable: true },
  { value: '1440x1080', label: '4:3 landscape — 1440×1080', width: 1440, height: 1080, selectable: true },
  { value: '1080x1620', label: '2:3 portrait — 1080×1620', width: 1080, height: 1620, selectable: false },
  { value: '1080x1080', label: '1:1 square — 1080×1080', width: 1080, height: 1080, selectable: false },
  { value: '1920x475', label: 'Hero banner — 1920×475', width: 1920, height: 475, selectable: false },
]

// Mirrors MAX_CANVAS_DIMENSION / MAX_CANVAS_PIXELS in matchcard_engine.py. The
// preview rejects anything beyond these, so the editor checks first rather than
// firing a request that's certain to fail on every keystroke.
export const MAX_CANVAS_DIMENSION = 8192
export const MAX_CANVAS_PIXELS = 16_777_216

/** Why a canvas can't be previewed, or null when it's fine. */
export function canvasProblem(canvas: { width: number; height: number }): string | null {
  const { width, height } = canvas
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1) {
    return 'Width and height must both be whole numbers above zero.'
  }
  if (width > MAX_CANVAS_DIMENSION || height > MAX_CANVAS_DIMENSION) {
    return `Each side must be ${MAX_CANVAS_DIMENSION}px or less — a 4K frame is 3840px.`
  }
  if (width * height > MAX_CANVAS_PIXELS) {
    return `${(width * height).toLocaleString()} pixels is too large to render; the limit is ${MAX_CANVAS_PIXELS.toLocaleString()}.`
  }
  return null
}

/** The aspect ratios MatchcardEngine can be asked for, as width/height. */
const SELECTABLE_ASPECTS = [16 / 9, 4 / 3]
const ASPECT_TOLERANCE = 0.01

/**
 * Whether the engine could ever select a template with this canvas. False means
 * the template renders fine in preview but no real card will use it until
 * _ASPECT_RATIOS gains the ratio and a caller requests it.
 */
export const canvasIsSelectable = (canvas: {
  width: number
  height: number
}): boolean => {
  if (!canvas.width || !canvas.height) return false
  const ratio = canvas.width / canvas.height
  return SELECTABLE_ASPECTS.some((r) => Math.abs(ratio - r) <= ASPECT_TOLERANCE)
}

// ---------------------------------------------------------------------------
// Positioning helpers
// ---------------------------------------------------------------------------

/** True when the layer positions relative to another layer. */
export const isAnchored = (layer: Layer): boolean =>
  isPlaced(layer) && Boolean(layer.anchor_to)

/**
 * Where a layer's absolute position lives. The engine reads
 * `box.at` first and falls back to `at` (resolve_position), so an image layer's
 * absolute position must be written to box.at or a box.at of [0,0] would win.
 */
export const absolutePositionField = (layer: PlacedLayer): 'box' | 'at' =>
  layer.type === 'image' ? 'box' : 'at'

export function readPosition(layer: PlacedLayer): Point {
  if (layer.anchor_to) return layer.anchor_to.offset ?? [0, 0]
  if (layer.type === 'image') return layer.box?.at ?? layer.at ?? [0, 0]
  return layer.at ?? [0, 0]
}

/** A patch that moves `layer` to `point`, in whichever field the engine reads. */
export function positionPatch(layer: PlacedLayer, point: Point): Record<string, unknown> {
  if (layer.anchor_to) {
    return { anchor_to: { ...layer.anchor_to, offset: point } }
  }
  if (layer.type === 'image') {
    return { box: { ...(layer.box ?? {}), at: point } }
  }
  return { at: point }
}

// ---------------------------------------------------------------------------
// Normalization + construction
// ---------------------------------------------------------------------------

const DEFAULT_CANVAS = { width: 1920, height: 1080 }

/**
 * Coerce a raw stored body into an editable TemplateBody with the required keys
 * present. Only fills in what the schema requires; it deliberately does not
 * strip unknown keys, because silently dropping a field an author (or a future
 * schema) added would lose work — publish reports those instead.
 */
export function normalizeTemplateBody(raw: unknown, fallbackName = ''): TemplateBody {
  const src = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const canvas = src.canvas as TemplateBody['canvas'] | undefined
  const targeting = (src.targeting ?? {}) as Partial<Targeting>

  return {
    ...(src as object),
    name: typeof src.name === 'string' && src.name ? src.name : fallbackName,
    canvas:
      canvas && typeof canvas.width === 'number' && typeof canvas.height === 'number'
        ? { width: canvas.width, height: canvas.height }
        : { ...DEFAULT_CANVAS },
    targeting: {
      ...targeting,
      types: Array.isArray(targeting.types) && targeting.types.length
        ? targeting.types
        : ['singles'],
      resolution: targeting.resolution ?? '1080',
    },
    layers: Array.isArray(src.layers) ? (src.layers as Layer[]) : [],
  } as TemplateBody
}

// The only top-level keys tc.artwork.template.v2 allows. Anything else fails
// publish, so the editor has to be able to name and drop them.
const KNOWN_TOP_LEVEL_KEYS = ['name', 'canvas', 'targeting', 'layers']

/**
 * Top-level keys the schema will reject. A v1 body opened in this editor still
 * carries `selector` / `dimensions` / `output_format`; normalization keeps them
 * rather than silently discarding an author's data, so the editor offers to drop
 * them instead — otherwise publish would be blocked with no way to fix it here.
 */
export const unknownTopLevelKeys = (body: TemplateBody | null): string[] =>
  body ? Object.keys(body).filter((k) => !KNOWN_TOP_LEVEL_KEYS.includes(k)) : []

/** Ascending z = back to front. Ties keep declaration order (stable sort). */
export const byZ = <T extends { layer: Layer }>(items: T[]): T[] =>
  [...items].sort((a, b) => zOf(a.layer) - zOf(b.layer))

export const zOf = (layer: Layer): number =>
  isPlaced(layer) ? (layer.z ?? 0) : 0

/**
 * The z a layer occupies for *presentation* ordering in the layer tree.
 *
 * A group carries no z of its own — the schema forbids one — but its contents
 * do, and the engine sorts every layer globally by z once groups are expanded.
 * So a group's place in the stack is where its contents start: the lowest z
 * among its descendants. Scoring a group as 0 instead files it behind the
 * background, which is how the doubles card came to list background first while
 * singles listed it last.
 *
 * A group spanning several z values (the doubles arms run 10 to 30) can't
 * occupy one slot faithfully; the lowest is where it enters the stack.
 */
export function layerStackZ(layer: Layer): number {
  if (isPlaced(layer)) return layer.z ?? 0
  const inner = descendantZs(layer)
  return inner.length ? Math.min(...inner) : 0
}

const descendantZs = (group: GroupLayer): number[] =>
  flattenLayers([...(group.then ?? []), ...(group.else ?? [])])
    .filter(({ layer }) => isPlaced(layer))
    .map(({ layer }) => (layer as PlacedLayer).z ?? 0)


const nextZ = (layers: Layer[]): number =>
  flattenLayers(layers).reduce((max, { layer }) => Math.max(max, zOf(layer)), 0) + 10

const uniqueId = (base: string, layers: Layer[]): string => {
  const existing = new Set(
    flattenLayers(layers)
      .map(({ layer }) => (isPlaced(layer) ? layer.id : null))
      .filter((id): id is string => Boolean(id))
  )
  if (!existing.has(base)) return base
  let n = 2
  while (existing.has(`${base}_${n}`)) n++
  return `${base}_${n}`
}

/** Build a new layer of `type` with defaults that render something visible. */
export function makeLayer(
  type: LayerType,
  layers: Layer[],
  canvas: { width: number; height: number }
): Layer {
  const z = nextZ(layers)
  const cx = Math.round(canvas.width / 2)
  const cy = Math.round(canvas.height / 2)

  switch (type) {
    case 'group':
      // Arms start empty; the tree offers an add button inside each.
      return {
        type: 'group',
        when: GROUP_CONDITIONS[0].build({ team: 'home' }),
        then: [],
        else: [],
      }
    case 'image':
      return {
        id: uniqueId('image', layers),
        type: 'image',
        z,
        box: { at: [cx - 200, cy - 200], size: [400, null] },
        source: { kind: 'fixed', tc_image_id: null },
      }
    case 'shape':
      return {
        id: uniqueId('shape', layers),
        type: 'shape',
        z,
        size: [600, 120],
        color: '#000000',
        opacity: 255,
        at: [cx - 300, cy - 60],
      }
    case 'text':
    default:
      return {
        id: uniqueId('text', layers),
        type: 'text',
        z,
        content: { bound: boundPath('home', 0, 'last_name') },
        font: { tc_font_id: null, size: 96 },
        max_width: 700,
        color: '#FFFFFF',
        transform: 'uppercase',
        at: [cx - 350, cy - 48],
      }
  }
}

/** 'canvas' is the engine's pseudo-layer for the whole frame, always present in
 *  its anchor map, so a real layer taking that id makes anchoring ambiguous. */
export const RESERVED_LAYER_IDS = ['canvas']

/**
 * Why an id can't be used, or null when it's fine.
 *
 * The v2 schema can only require minLength: 1 — JSON Schema can't express
 * uniqueness across an array — so nothing server-side rejects a collision. The
 * engine keys its anchor map by id, so two layers sharing one means the
 * later-drawn wins and every anchor_to pointing at that id silently resolves to
 * the wrong rectangle. Caught here or not at all.
 */
export function layerIdProblem(
  id: string,
  layers: Layer[],
  ownPath: LayerPath
): string | null {
  const trimmed = id.trim()
  if (!trimmed) return 'An ID is required — the schema rejects an empty one.'
  if (trimmed !== id) return 'Leading or trailing spaces will be part of the ID.'
  if (RESERVED_LAYER_IDS.includes(trimmed)) {
    return `“${trimmed}” is reserved for the frame itself — anchoring couldn’t tell them apart.`
  }
  const clash = flattenLayers(layers).find(
    ({ layer, path }) =>
      isPlaced(layer) && layer.id === trimmed && !samePath(path, ownPath)
  )
  if (clash) {
    return 'Another layer already uses this ID. The engine keys anchors by ID, so one would shadow the other.'
  }
  return null
}

/**
 * Rename a layer and repoint everything that referenced it.
 *
 * anchor_to.layer and skip_if_fell_back hold ids, so renaming without this
 * leaves dangling references — and a dangling anchor doesn't error, it silently
 * falls back to the absolute position (resolve_position returns `at` when the
 * reference has no box).
 */
export function renameLayerId(
  layers: Layer[],
  path: LayerPath,
  nextId: string
): Layer[] {
  const target = getLayerAt(layers, path)
  if (!target || !isPlaced(target)) return layers
  const previousId = target.id
  const renamed = patchLayerAt(layers, path, { id: nextId })
  if (!previousId || previousId === nextId) return renamed

  const repoint = (list: Layer[]): Layer[] =>
    list.map((layer) => {
      if (isGroup(layer)) {
        return {
          ...layer,
          ...(layer.then ? { then: repoint(layer.then) } : {}),
          ...(layer.else ? { else: repoint(layer.else) } : {}),
        }
      }
      let next = layer
      if (next.anchor_to?.layer === previousId) {
        next = { ...next, anchor_to: { ...next.anchor_to, layer: nextId } }
      }
      if (isImage(next) && next.skip_if_fell_back === previousId) {
        next = { ...next, skip_if_fell_back: nextId }
      }
      return next
    })
  return repoint(renamed)
}

/** A human label for a layer in the tree. */
export function layerLabel(layer: Layer): string {
  if (isGroup(layer)) {
    return `if ${layer.when?.path ?? '—'}`
  }
  return layer.id
}

/** A one-line summary of what a layer will show, for the tree's second line. */
export function layerSubtitle(layer: Layer): string {
  if (isText(layer)) {
    return 'static' in layer.content
      ? `“${layer.content.static}”`
      : layer.content.bound
  }
  if (isImage(layer)) {
    const s = layer.source
    if (s.kind === 'fixed') return s.label ?? (s.tc_image_id ? 'fixed image' : 'no image')
    if (s.kind === 'entity') return `${s.entity} · ${s.asset}`
    return `${s.rules?.length ?? 0} category rule(s)`
  }
  if (isShape(layer)) {
    return `${layer.size?.[0] ?? 0}×${layer.size?.[1] ?? 0}`
  }
  return ''
}
